import { useEffect, useRef, useState } from "react";

import { TicketingFormCategory } from "@simplyk/common";
import { produce } from "immer";
import { FieldValues, Path, PathValue, UseFormReturn } from "react-hook-form";

import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { trpc } from "@/common/helpers/trpc";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DonationFormWithUserOrOrgAccessOutput, TicketingOutput } from "@/common/types/trpc";
import { getDonationFormEmailTemplates } from "@/features/LiveFormEditor/common/getDonationFormEmailTemplates";
import { getTicketingThankYouEmailTemplates } from "@/features/LiveFormEditor/common/getTicketingThankYouEmailTemplates";
import { useLiveFormEditorContext } from "@/features/LiveFormEditor/common/LiveFormEditorContext";
import { LiveEditorStepKey } from "@/features/LiveFormEditor/Steps/LiveEditorStep.enum";
import { LiveDonationData, LiveTicketingData } from "@/features/LiveFormEditor/types/LiveFormEditor.types";

export const useApplyThankYouAIDefaults = <T extends FieldValues>(
  form: UseFormReturn<T>,
  stepKey: LiveEditorStepKey = "thank-you-email",
  fieldPaths: { subject: string; content: string } = { subject: "thankYou.subject", content: "thankYou.content" }
) => {
  const { t } = useTranslate();
  const { currentUser, organization } = useCurrentUserContext();
  const {
    apiFormObject,
    formType,
    stepProgress,
    editOrCreateMode,
    getIndexStepFromKey,
    liveFormData,
    setLiveFormData,
    isCreatedFromDuplicate,
  } = useLiveFormEditorContext();
  const { mutateAsync: getFormGeneratedByAIData } = trpc.getFormGeneratedByAIData.useMutation();

  const hasAlreadySavedStep = getIndexStepFromKey(stepKey) <= stepProgress.saved;

  const shouldApply =
    Boolean(organization?.npoContext) &&
    !(
      formType === "ticketing" && (apiFormObject as TicketingOutput).formCategory === TicketingFormCategory.Sweepstakes
    ) &&
    !hasAlreadySavedStep &&
    editOrCreateMode === "create" &&
    !isCreatedFromDuplicate;

  const hasAppliedRef = useRef(false);
  const [isAIDefaultLoading, setIsGenerating] = useState(shouldApply);

  useEffect(() => {
    if (!shouldApply || hasAppliedRef.current) {
      return;
    }
    hasAppliedRef.current = true;

    let mounted = true;

    setIsGenerating(true);

    const fetchAndApply = async () => {
      const response = await getFormGeneratedByAIData({ formId: apiFormObject.id });

      if (!mounted) {
        return;
      }

      const fallback =
        formType === "ticketing"
          ? getTicketingThankYouEmailTemplates({
              category: (apiFormObject as TicketingOutput).formCategory,
              t,
              organization,
              currentUser,
            })
          : getDonationFormEmailTemplates(
              (apiFormObject as DonationFormWithUserOrOrgAccessOutput).category,
              t,
              currentUser
            );

      const subject =
        response?.thankYouEmailSubject ?? (form.getValues(fieldPaths.subject as Path<T>) || fallback.emailObject);
      const content =
        response?.thankYouEmailContent ?? (form.getValues(fieldPaths.content as Path<T>) || fallback.emailContent);

      form.setValue(fieldPaths.subject as Path<T>, subject as PathValue<T, Path<T>>, { shouldDirty: true });
      form.setValue(fieldPaths.content as Path<T>, content as PathValue<T, Path<T>>, { shouldDirty: true });
      setIsGenerating(false);
    };

    fetchAndApply();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldApply, apiFormObject.id]);

  useEffect(() => {
    if (!liveFormData) {
      return;
    }

    const currentValue = (liveFormData as LiveDonationData | LiveTicketingData).automatedCommunicationsData
      .isAIDefaultLoading;
    if (currentValue === isAIDefaultLoading) {
      return;
    }

    const updated = produce(liveFormData as LiveDonationData | LiveTicketingData, (draft) => {
      draft.automatedCommunicationsData.isAIDefaultLoading = isAIDefaultLoading;
    });
    setLiveFormData(updated as Parameters<typeof setLiveFormData>[0]);
  }, [isAIDefaultLoading, liveFormData, setLiveFormData]);

  return { isAIEligible: shouldApply, isAIDefaultLoading };
};
